import { useState, useEffect, useCallback } from 'react'

interface UseFetchOptions<T> {
  initialData?: T
  onSuccess?: (data: T) => void
  onError?: (error: Error) => void
  enabled?: boolean
}

interface UseFetchResult<T> {
  data: T | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
  setData: (data: T | null) => void
}

export function useFetch<T>(
  fetchFn: () => Promise<T>,
  options: UseFetchOptions<T> = {}
): UseFetchResult<T> {
  const { initialData = null, onSuccess, onError, enabled = true } = options

  const [data, setData] = useState<T | null>(initialData)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await fetchFn()
      setData(result)
      onSuccess?.(result)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error')
      setError(error)
      onError?.(error)
    } finally {
      setLoading(false)
    }
  }, [fetchFn, onSuccess, onError])

  useEffect(() => {
    if (enabled) {
      fetchData()
    }
  }, [enabled, fetchData])

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    setData,
  }
}

// 用于分页数据的 hook
interface UsePaginatedFetchOptions<T> extends UseFetchOptions<T> {
  page?: number
  limit?: number
}

interface UsePaginatedFetchResult<T> extends UseFetchResult<T> {
  page: number
  setPage: (page: number) => void
  hasMore: boolean
}

export function usePaginatedFetch<T extends { data: unknown[]; total: number }>(
  fetchFn: (page: number, limit: number) => Promise<T>,
  options: UsePaginatedFetchOptions<T> = {}
): UsePaginatedFetchResult<T> {
  const { initialData = null, onSuccess, onError, enabled = true, page: initialPage = 1, limit = 10 } = options

  const [data, setData] = useState<T | null>(initialData)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [page, setPage] = useState(initialPage)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await fetchFn(page, limit)
      setData(result)
      onSuccess?.(result)
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error')
      setError(error)
      onError?.(error)
    } finally {
      setLoading(false)
    }
  }, [fetchFn, page, limit, onSuccess, onError])

  useEffect(() => {
    if (enabled) {
      fetchData()
    }
  }, [enabled, fetchData, page])

  const hasMore = data ? page * limit < data.total : false

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    setData,
    page,
    setPage,
    hasMore,
  }
}

// 用于突变操作的 hook
interface UseMutationOptions<T, V> {
  onSuccess?: (data: T, variables: V) => void
  onError?: (error: Error, variables: V) => void
}

interface UseMutationResult<T, V> {
  mutate: (variables: V) => Promise<T>
  loading: boolean
  error: Error | null
  data: T | null
  reset: () => void
}

export function useMutation<T, V = unknown>(
  mutationFn: (variables: V) => Promise<T>,
  options: UseMutationOptions<T, V> = {}
): UseMutationResult<T, V> {
  const { onSuccess, onError } = options

  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutate = useCallback(
    async (variables: V) => {
      setLoading(true)
      setError(null)

      try {
        const result = await mutationFn(variables)
        setData(result)
        onSuccess?.(result, variables)
        return result
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error')
        setError(error)
        onError?.(error, variables)
        throw error
      } finally {
        setLoading(false)
      }
    },
    [mutationFn, onSuccess, onError]
  )

  const reset = useCallback(() => {
    setData(null)
    setLoading(false)
    setError(null)
  }, [])

  return {
    mutate,
    loading,
    error,
    data,
    reset,
  }
}
